// traceRecallOps.js — what the scan window's agents asked ORIGIN ITSELF, and
// what its memory handed back. The Recall X-ray rides this lib: consultations
// of the Origin MCP server's self-knowledge surface —
//
//   search_prior_work — the prior-work retrieval trial. The ARGS are the
//     richest intent capture anywhere in the traffic: `request` is the agent's
//     own articulation of its task and `facets` its expansion into adjacent
//     concerns. The RESULT is the recalled world: routed topics, neighbour
//     topics, and candidate sessions (author/date/summary/session_id) with the
//     server's disclosed drop counts.
//   analytics_describe_schema / _entities / analytics_query_guide — the
//     analytics-docs lookups. Their payloads are static self-description
//     (identical for every tenant), so ops record THAT they were consulted and
//     with which entity names — never the doc text.
//
// analytics_query itself is deliberately absent: running a data query is the
// Queries tab's story; this tab is the agent consulting Origin's memory and
// manual before (or instead of) acting.
//
// Same extract → collectWindow → build contract as the sibling trace libs.
//   op = { key, kind: search|docs, tool, ok, … } — search ops carry
//   { request, facets, mode, windowDays, maxCandidates, result, view };
//   docs ops carry { entities } and no payload.

// Raw response text kept per search op (the Raw toggle's fallback; the parsed
// view carries the full structure separately).
const RESULT_CAP = 4000;
// Bundle candidates kept per consultation. The server clamps returns to 300
// and defaults to 100; keeping 150 covers the defaults exactly while bounding
// scan state on a window full of consultations.
const CANDIDATE_CAP = 150;
const REQUEST_CAP = 2000;
const FACET_CAP = 24;
const FACET_TEXT_CAP = 160;

function str(v) {
  return typeof v === "string" && v ? v : null;
}

function num(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function cap(s, max) {
  const t = typeof s === "string" ? s : "";
  return { text: t.length > max ? t.slice(0, max) : t, truncated: t.length > max };
}

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n");
  }
  return "";
}

function resultErrored(result) {
  if (!result) return false;
  if (result.isError) return true;
  return contentText(result.content).includes("<tool_use_error>");
}

// djb2 — stable synthetic dedup key (same as the sibling libs).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// The recall tool behind a wire name, or null. Wire aliases vary wildly on
// real traffic — mcp__origin-staging__X, mcp__<uuid>__X, mcp__claude_ai_…__X,
// bare X, even Server:X — so match the LAST name segment exactly.
const DOCS_TOOLS = {
  analytics_describe_schema: "schema map",
  analytics_describe_entities: "entity definitions",
  analytics_query_guide: "query guide",
};

export function recallToolOf(name) {
  const last = String(name || "").split("__").pop().split(":").pop().toLowerCase();
  if (last === "search_prior_work") return { kind: "search", tool: "prior work" };
  if (DOCS_TOOLS[last]) return { kind: "docs", tool: DOCS_TOOLS[last] };
  return null;
}

// ── prior-work result view ───────────────────────────────────────────────────
// The server emits three payload shapes (bundle / map / no-precedent) plus one
// client-side artifact: an MCP-output SPILL, when the harness wrote the
// oversized result to a file instead of inlining it. Two marker shapes on
// real traffic: `<persisted-output>…` and `Error: result (N characters…)
// exceeds maximum allowed tokens. Output has been saved to <path>`. Parse
// into a typed view; null = unrecognized (raw text stands alone).

export function parsePriorWorkResult(text) {
  const s = typeof text === "string" ? text : "";
  if (!s) return null;
  const head = s.trimStart();
  if (head.startsWith("<persisted-output>")
    || (head.startsWith("Error: result (") && head.includes("exceeds maximum allowed tokens"))) {
    return { type: "spilled" };
  }
  let d = null;
  try { d = JSON.parse(s); } catch { return null; }
  if (!d || typeof d !== "object" || Array.isArray(d)) return null;
  if (!("routed_topics" in d) && !("candidates" in d)) return null;

  const topics = (Array.isArray(d.routed_topics) ? d.routed_topics : []).map((t) => ({
    id: num(t?.cluster_id),
    name: str(t?.name),
    description: str(t?.description),
    points: num(t?.point_count),
    level: num(t?.level),
  }));
  const neighbours = (Array.isArray(d.neighborhood) ? d.neighborhood : []).map((n) => ({
    id: num(n?.cluster_id),
    name: str(n?.name),
    sharedSessions: num(n?.shared_sessions),
  }));
  const allCandidates = Array.isArray(d.candidates) ? d.candidates : null;
  const candidates = allCandidates
    ? allCandidates.slice(0, CANDIDATE_CAP).map((c) => ({
      date: str(c?.date),
      sessionId: str(c?.session_id),
      author: str(c?.author),
      topic: str(c?.topic),
      summary: str(c?.summary),
    }))
    : null;
  const disclosures = d.disclosures && typeof d.disclosures === "object"
    ? {
      returned: num(d.disclosures.returned),
      perTopicCap: num(d.disclosures.per_topic_cap),
      droppedDuplicates: num(d.disclosures.dropped_duplicates),
      droppedPerTopicCap: num(d.disclosures.dropped_per_topic_cap),
      droppedOverGlobalCap: num(d.disclosures.dropped_over_global_cap),
    }
    : null;

  return {
    // A bundle/map with no routed topics is the server's "no precedent" answer.
    type: topics.length === 0 ? "none" : d.mode === "map" ? "map" : "bundle",
    windowDays: num(d.window_days),
    catalogTruncated: Boolean(d.catalog_truncated),
    topics,
    neighbours,
    candidates,
    candidatesTruncated: Boolean(allCandidates && allCandidates.length > CANDIDATE_CAP),
    disclosures,
    usageNote: str(d.usage_note),
  };
}

// ── extraction ───────────────────────────────────────────────────────────────

export function extractRecallOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const recall = recallToolOf(b.name);
      if (!recall) continue;
      const input = b.input && typeof b.input === "object" ? b.input : {};
      const result = b.id ? resultById.get(b.id) : null;
      const ok = !resultErrored(result);
      const key = b.id || `synth:${hashString(String(b.name) + JSON.stringify(input))}`;

      if (recall.kind === "docs") {
        const entities = Array.isArray(input.entities)
          ? input.entities.map((e) => str(e)).filter(Boolean).slice(0, 64)
          : [];
        ops.push({ key, kind: "docs", tool: recall.tool, entities, ok });
        continue;
      }

      const request = cap(str(input.request) || "", REQUEST_CAP);
      const facets = (Array.isArray(input.facets) ? input.facets : [])
        .map((f) => str(f))
        .filter(Boolean)
        .slice(0, FACET_CAP)
        .map((f) => cap(f, FACET_TEXT_CAP).text);
      const fullText = ok && result ? contentText(result.content) : "";
      const res = cap(fullText, RESULT_CAP);
      ops.push({
        key,
        kind: "search",
        tool: recall.tool,
        request: request.text,
        requestTruncated: request.truncated,
        facets,
        mode: str(input.mode) === "map" ? "map" : "bundle",
        windowDays: num(input.time_window_days),
        maxCandidates: num(input.max_candidates),
        ok,
        result: { text: res.text || null, truncated: res.truncated },
        view: fullText ? parsePriorWorkResult(fullText) : null,
      });
    }
  }
  return ops;
}

// First-sighting window dedup — bodies are cumulative per session, so the same
// tool_use id resurfaces in later prompts of the session (same contract as the
// sibling libs).
export function collectWindowRecallOps(promptEvents) {
  const tsOf = (t) => (typeof t === "number" ? t : t ? Date.parse(t) : null);
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seen = new Set();
  const flat = [];
  for (const p of sorted) {
    for (const op of p.ops || []) {
      const dedupKey = `${p.sessionId || ""}|${op.key}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      flat.push({
        ...op,
        promptId: p.promptId,
        sessionId: p.sessionId || "",
        endpointId: p.endpointId || "",
        endpointName: p.endpointName || "",
        ts: p.ts ?? null,
      });
    }
  }
  return flat;
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "unknown";
}

// candidateRank keeps this many top rows (the panel renders fewer).
const CANDIDATE_RANK_CAP = 30;

// The window's recall activity:
//   consultations: search ops newest-first — each one a self-contained
//                  deliverable (request + facets in, topics + candidates out)
//   topicRank:     which memory topics the window kept routing to
//   citedAuthors:  whose prior work keeps being surfaced (the "who to talk to"
//                  pointer the tool's own description asks callers to keep)
//   candidateRank: the most-recalled prior work — candidate SESSIONS ranked by
//                  how many distinct consultations recalled them (a session
//                  can surface as several prompt rows; identity is session_id,
//                  the pointer a reader would actually go open)
//   recallCounts:  { sessionId: consultation-count } for every recalled
//                  session — lets a consultation view flag its own candidates
//                  that the whole window kept coming back to
//   docs:          the manual lookups, rolled up per doc
//   actors:        Who-rail rows; totals: the header vitals
export function buildRecallActivity(flatOps) {
  const tsOf = (t) => (typeof t === "number" ? t : t ? Date.parse(t) : null);
  const consultations = [];
  const docsByTool = new Map();
  const topicCount = new Map();
  const authorCount = new Map();
  const candMap = new Map();
  const actors = new Map();
  const sessions = new Set();
  let noPrecedent = 0;
  let mapOnly = 0;
  let spilled = 0;
  let candidatesReturned = 0;
  let errored = 0;

  for (const op of flatOps || []) {
    sessions.add(op.sessionId);
    let actor = actors.get(op.endpointId);
    if (!actor) {
      actor = { id: op.endpointId, name: op.endpointName || shortId(op.endpointId), searches: 0, docs: 0, sessions: new Set() };
      actors.set(op.endpointId, actor);
    }
    actor.sessions.add(op.sessionId);
    if (!op.ok) errored += 1;

    if (op.kind === "docs") {
      actor.docs += 1;
      let d = docsByTool.get(op.tool);
      if (!d) {
        d = { tool: op.tool, count: 0, entities: new Set(), actors: new Set(), lastTs: null };
        docsByTool.set(op.tool, d);
      }
      d.count += 1;
      for (const e of op.entities || []) d.entities.add(e);
      d.actors.add(op.endpointId);
      if ((tsOf(op.ts) ?? 0) > (tsOf(d.lastTs) ?? 0)) d.lastTs = op.ts;
      continue;
    }

    actor.searches += 1;
    const view = op.view;
    if (view?.type === "none") noPrecedent += 1;
    if (view?.type === "map") mapOnly += 1;
    if (view?.type === "spilled") spilled += 1;
    if (view?.candidates) candidatesReturned += view.candidates.length;
    for (const t of view?.topics || []) {
      if (!t.name) continue;
      const e = topicCount.get(t.name) || { name: t.name, count: 0, points: t.points ?? null };
      e.count += 1;
      topicCount.set(t.name, e);
    }
    for (const c of view?.candidates || []) {
      if (!c.author) continue;
      authorCount.set(c.author, (authorCount.get(c.author) || 0) + 1);
    }
    // Count each recalled session once PER CONSULTATION (a session often
    // surfaces as several prompt rows within one result).
    const seenThisConsult = new Set();
    for (const c of view?.candidates || []) {
      if (!c.sessionId || seenThisConsult.has(c.sessionId)) continue;
      seenThisConsult.add(c.sessionId);
      let e = candMap.get(c.sessionId);
      if (!e) {
        e = { sessionId: c.sessionId, count: 0, author: c.author || null, date: c.date || null, summaries: new Map() };
        candMap.set(c.sessionId, e);
      }
      e.count += 1;
      if (!e.author && c.author) e.author = c.author;
      // Server dates are "YYYY-MM-DD hh:mm:ss.SSS" — lexicographic max = latest.
      if ((c.date || "") > (e.date || "")) e.date = c.date;
      if (c.summary) e.summaries.set(c.summary, (e.summaries.get(c.summary) || 0) + 1);
    }
    consultations.push(op);
  }

  consultations.sort((a, b) => (tsOf(b.ts) ?? 0) - (tsOf(a.ts) ?? 0));
  const topicRank = [...topicCount.values()].sort((a, b) => (b.count - a.count) || String(a.name).localeCompare(String(b.name)));
  const citedAuthors = [...authorCount.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name));
  const candidateRank = [...candMap.values()]
    .map((e) => ({
      sessionId: e.sessionId,
      count: e.count,
      author: e.author,
      date: e.date,
      // Representative summary: the variant seen most often for this session.
      summary: [...e.summaries.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null,
    }))
    .sort((a, b) => (b.count - a.count) || String(b.date || "").localeCompare(String(a.date || "")))
    .slice(0, CANDIDATE_RANK_CAP);
  const recallCounts = {};
  for (const [sid, e] of candMap) recallCounts[sid] = e.count;
  const docs = [...docsByTool.values()]
    .map((d) => ({ ...d, entities: [...d.entities], actors: d.actors.size }))
    .sort((a, b) => b.count - a.count);
  const actorList = [...actors.values()]
    .filter((a) => a.searches + a.docs > 0)
    .map((a) => ({ id: a.id, name: a.name, searches: a.searches, docs: a.docs, sessions: a.sessions.size }));
  actorList.sort((a, b) => (b.searches - a.searches) || (b.docs - a.docs) || a.name.localeCompare(b.name));

  return {
    consultations,
    topicRank,
    citedAuthors,
    candidateRank,
    recallCounts,
    docs,
    actors: actorList,
    totals: {
      searches: consultations.length,
      docs: docs.reduce((s, d) => s + d.count, 0),
      noPrecedent,
      mapOnly,
      spilled,
      candidates: candidatesReturned,
      errored,
      sessions: sessions.size,
      actors: actorList.length,
    },
  };
}
